import i18n from '@/i18n'

export const WORKSPACE_PREFERENCES_KEY = 'workspace_preferences_v1'
export const RECENT_PAGES_KEY = 'workspace_recent_pages_v1'
const APP_LANG_KEY = 'app-lang'

export function defaultWorkspacePreferences() {
  return {
    language: 'en',
    timeFormat: '24h',
    emailNotifications: true,
    desktopNotifications: false,
    reviewAlerts: true,
    translationAlerts: true,
    rememberSidebar: true,
    startMinimizedSidebar: false,
    keepNavigationContext: true,
    defaultLandingPage: '/dashboard',
    itemsPerPage: 20,
    rememberRecentPages: true,
    storeDraftRecovery: true,
    confirmBeforeSignOut: true
  }
}

export function normalizeWorkspacePreferences(value) {
  const defaults = defaultWorkspacePreferences()
  const next = Object.assign({}, defaults, value || {})
  next.language = next.language === 'th' ? 'th' : 'en'
  next.timeFormat = next.timeFormat === '12h' ? '12h' : '24h'
  next.itemsPerPage = [10, 20, 50, 100].includes(Number(next.itemsPerPage)) ? Number(next.itemsPerPage) : defaults.itemsPerPage
  ;[
    'emailNotifications',
    'desktopNotifications',
    'reviewAlerts',
    'translationAlerts',
    'rememberSidebar',
    'startMinimizedSidebar',
    'keepNavigationContext',
    'rememberRecentPages',
    'storeDraftRecovery',
    'confirmBeforeSignOut'
  ].forEach((key) => {
    next[key] = !!next[key]
  })
  next.defaultLandingPage = String(next.defaultLandingPage || defaults.defaultLandingPage).trim() || defaults.defaultLandingPage
  return next
}

export function readWorkspacePreferences() {
  if (typeof window === 'undefined' || !window.localStorage) {
    return defaultWorkspacePreferences()
  }
  try {
    const raw = window.localStorage.getItem(WORKSPACE_PREFERENCES_KEY)
    return normalizeWorkspacePreferences(raw ? JSON.parse(raw) : {})
  } catch (error) {
    return defaultWorkspacePreferences()
  }
}

export function saveWorkspacePreferences(value) {
  if (typeof window === 'undefined' || !window.localStorage) return defaultWorkspacePreferences()
  const next = normalizeWorkspacePreferences(value)
  window.localStorage.setItem(WORKSPACE_PREFERENCES_KEY, JSON.stringify(next))
  return next
}

export function applyWorkspacePreferences({ store, i18nInstance = i18n } = {}) {
  const preferences = readWorkspacePreferences()
  if (typeof window !== 'undefined' && window.localStorage) {
    window.localStorage.setItem(APP_LANG_KEY, preferences.language)
  }
  if (i18nInstance && i18nInstance.locale !== preferences.language) {
    i18nInstance.locale = preferences.language
  }
  if (store && store.commit) {
    store.commit('set', ['sidebarMinimize', !!preferences.startMinimizedSidebar])
  }
  return preferences
}

export function getPreferredItemsPerPage(fallback = 10) {
  return readWorkspacePreferences().itemsPerPage || fallback
}

export function shouldConfirmBeforeSignOut() {
  return !!readWorkspacePreferences().confirmBeforeSignOut
}

export function shouldKeepNavigationContext() {
  return !!readWorkspacePreferences().keepNavigationContext
}

export function isAlertEnabled(type) {
  const preferences = readWorkspacePreferences()
  if (type === 'translation') return !!preferences.translationAlerts
  if (type === 'review') return !!preferences.reviewAlerts
  return true
}

export function getPreferredLandingPage(fallback = '/dashboard') {
  const preferences = readWorkspacePreferences()
  return preferences.defaultLandingPage || fallback
}

export function getPreferredLocale() {
  return readWorkspacePreferences().language === 'th' ? 'th-TH' : 'en-US'
}

export function use12HourTime() {
  return readWorkspacePreferences().timeFormat === '12h'
}

export function formatDateTimeWithPreferences(value, options = {}) {
  if (!value) return '-'
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  const locale = getPreferredLocale()
  const hour12 = use12HourTime()
  return date.toLocaleString(locale, Object.assign({
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12
  }, options))
}

export function formatTimeWithPreferences(value, options = {}) {
  if (!value) return '-'
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleTimeString(getPreferredLocale(), Object.assign({
    hour: '2-digit',
    minute: '2-digit',
    hour12: use12HourTime()
  }, options))
}

export function rememberVisitedPage(route) {
  if (typeof window === 'undefined' || !window.localStorage) return
  const preferences = readWorkspacePreferences()
  if (!preferences.rememberRecentPages) {
    window.localStorage.removeItem(RECENT_PAGES_KEY)
    return
  }
  const path = route && route.fullPath ? String(route.fullPath) : ''
  if (!path || path.startsWith('/pages/')) return
  let items = []
  try {
    items = JSON.parse(window.localStorage.getItem(RECENT_PAGES_KEY) || '[]')
  } catch (error) {
    items = []
  }
  const nextItems = [{ path, at: new Date().toISOString() }]
    .concat(items.filter(item => item && item.path !== path))
    .slice(0, 12)
  window.localStorage.setItem(RECENT_PAGES_KEY, JSON.stringify(nextItems))
}

export function isDesktopNotificationEnabled() {
  return !!readWorkspacePreferences().desktopNotifications
}

export function maybeShowDesktopNotification(payload = {}) {
  if (typeof window === 'undefined' || typeof Notification === 'undefined') return false
  if (!isDesktopNotificationEnabled()) return false
  if (Notification.permission !== 'granted') return false
  const title = payload.title || 'Workspace update'
  const options = {
    body: payload.body || payload.message || '',
    icon: payload.icon || '/img/favicon.png'
  }
  try {
    const notification = new Notification(title, options)
    if (typeof payload.onClick === 'function') {
      notification.onclick = (event) => {
        if (event && typeof event.preventDefault === 'function') event.preventDefault()
        payload.onClick(notification, event)
      }
    }
    return true
  } catch (error) {
    return false
  }
}
